import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useConfig } from '../../contexts/ConfigContext';
import { ArrowRight, Calendar } from 'lucide-react';
import { Reveal } from './Reveal';

export default function FinalCTA() {
  const { config } = useConfig();
  const allowSelfSignup = config?.enable_self_signup === true;
  const allowPilot = config?.enable_pilot_requests !== false;
  const contactEmail = config?.contact_email || 'info@complytradeiq.com';
  const contactHref = `mailto:${contactEmail}?subject=ComplyTradeIQ%20demo%20request`;

  return (
    <section className="relative py-28 md:py-36 overflow-hidden">
      <motion.div
        animate={{ opacity: [0.4, 0.7, 0.4] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-72 bg-gradient-to-b from-cyan-glow/5 via-cyan-glow/10 to-transparent blur-3xl pointer-events-none"
      />

      <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
        <Reveal>
          <div className="text-[11px] uppercase tracking-[0.4em] text-cyan-glow font-mono mb-6">
            {allowSelfSignup ? 'Pre-shipment review platform' : 'Private pilot - Invite-only'}
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.06] tracking-tight">
            Catch shipment risks{' '}
            <span className="text-cyan-glow text-glow">before customs does</span>
          </h2>
        </Reveal>

        <Reveal delay={0.15}>
          <p className="mt-7 text-base md:text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
            We are onboarding a small group of import/export and compliance teams to the ComplyTradeIQ pilot.
            Tell us about your operation and our compliance team will be in touch.
          </p>
        </Reveal>

        <Reveal delay={0.25}>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            {allowSelfSignup ? (
              <Link
                to="/signup"
                className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-4 rounded-lg bg-cyan-glow text-navy-950 font-semibold transition-all glow-cyan hover:bg-cyan-hover hover:scale-[1.03] active:scale-[0.99]"
                data-testid="final-cta-signup"
              >
                Start Validation
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            ) : allowPilot ? (
              <Link
                to="/request-access"
                className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-4 rounded-lg bg-cyan-glow text-navy-950 font-semibold transition-all glow-cyan hover:bg-cyan-hover hover:scale-[1.03] active:scale-[0.99]"
                data-testid="final-cta-request"
              >
                Request Pilot Access
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            ) : null}
            <a
              href={contactHref}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-4 rounded-lg border border-white/15 text-white hover:bg-white/5 hover:border-white/25 transition-colors"
              data-testid="final-cta-talk"
            >
              <Calendar className="h-4 w-4" />
              Talk to compliance team
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.35}>
          <div className="mt-10 text-[11px] text-slate-500 font-mono tracking-[0.2em] uppercase">
            AI interprets - Rules decide - Humans act
          </div>
        </Reveal>
      </div>
    </section>
  );
}
